import type {
  AuthAvailabilityController,
  AuthAvailabilityListener,
  AuthAvailabilityState,
} from "./types";

const authRetryConfig = {
  initialDelayMs: 1_000,
  maxDelayMs: 30_000,
} as const;

const authAvailabilityControllers = new Map<
  string,
  AuthAvailabilityController
>();

/**
 * Create the discovery endpoint URL for a given authority.
 */
export function createDiscoveryEndpointUrl(authority: string): string {
  return `${authority.replace(/\/+$/, "")}/.well-known/openid-configuration`;
}

/**
 * Calculate retry delay in milliseconds using exponential backoff.
 */
export function getRetryDelayMs(failureCount: number): number {
  return Math.min(
    authRetryConfig.initialDelayMs * 2 ** Math.max(failureCount - 1, 0),
    authRetryConfig.maxDelayMs,
  );
}

/**
 * Extract a user-friendly error message from an error object.
 */
export function getAuthAvailabilityErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "The authentication service is currently unavailable.";
}

/**
 * Check if the OIDC discovery endpoint is available.
 */
export async function ensureOidcEndpointAvailable(
  authority: string,
): Promise<void> {
  const response = await fetch(createDiscoveryEndpointUrl(authority), {
    method: "GET",
    headers: {
      Accept: "application/json",
    },
    cache: "no-store",
  });

  if (!response.ok) {
    throw new Error(
      `Authentication discovery failed with status ${response.status}.`,
    );
  }
}

/**
 * Notify all listeners of a state change in the controller.
 */
function notifyAuthAvailabilityListeners(
  controller: AuthAvailabilityController,
): void {
  for (const listener of controller.listeners) {
    listener(controller.state);
  }
}

/**
 * Update the availability state and notify listeners.
 */
export function setAuthAvailabilityState(
  controller: AuthAvailabilityController,
  state: AuthAvailabilityState,
): void {
  controller.state = state;
  notifyAuthAvailabilityListeners(controller);
}

/**
 * Get or create the availability controller for a given authority.
 * Controllers are cached per authority to prevent duplicate probes on remount.
 */
export function getAuthAvailabilityController(
  authority: string,
): AuthAvailabilityController {
  const existingController = authAvailabilityControllers.get(authority);
  if (existingController) {
    return existingController;
  }

  const controller: AuthAvailabilityController = {
    authority,
    state: {
      status: "checking",
      failureCount: 0,
      nextRetryDelayMs: null,
      errorMessage: null,
    },
    failureCount: 0,
    isStarted: false,
    retryTimer: undefined,
    currentProbe: null,
    listeners: new Set(),
  };

  authAvailabilityControllers.set(authority, controller);
  return controller;
}

/**
 * Schedule or return the current probe promise for the controller.
 * Implements exponential backoff retry logic with deduplication.
 */
export function scheduleAuthAvailabilityProbe(
  controller: AuthAvailabilityController,
): Promise<void> {
  if (controller.currentProbe) {
    return controller.currentProbe;
  }

  controller.currentProbe = (async () => {
    setAuthAvailabilityState(controller, {
      status: controller.failureCount > 0 ? "retrying" : "checking",
      failureCount: controller.failureCount,
      nextRetryDelayMs: null,
      errorMessage: controller.state.errorMessage,
    });

    try {
      await ensureOidcEndpointAvailable(controller.authority);

      setAuthAvailabilityState(controller, {
        status: "ready",
        failureCount: controller.failureCount,
        nextRetryDelayMs: null,
        errorMessage: null,
      });
    } catch (error) {
      controller.failureCount += 1;
      const nextRetryDelayMs = getRetryDelayMs(controller.failureCount);

      setAuthAvailabilityState(controller, {
        status: "retrying",
        failureCount: controller.failureCount,
        nextRetryDelayMs,
        errorMessage: getAuthAvailabilityErrorMessage(error),
      });

      if (controller.retryTimer === undefined) {
        controller.retryTimer = window.setTimeout(() => {
          controller.retryTimer = undefined;
          void scheduleAuthAvailabilityProbe(controller);
        }, nextRetryDelayMs);
      }
    } finally {
      controller.currentProbe = null;
    }
  })();

  return controller.currentProbe;
}

/**
 * Subscribe to availability state changes for a given authority.
 * Returns an unsubscribe function.
 * Automatically starts the probe on first subscription.
 */
export function subscribeToAuthAvailability(
  authority: string,
  listener: AuthAvailabilityListener,
): () => void {
  const controller = getAuthAvailabilityController(authority);
  controller.listeners.add(listener);
  listener(controller.state);

  if (!controller.isStarted) {
    controller.isStarted = true;
    void scheduleAuthAvailabilityProbe(controller);
  }

  // Listen for manual retry triggers
  const handleRetryNow = () => {
    if (controller.retryTimer !== undefined) {
      window.clearTimeout(controller.retryTimer);
      controller.retryTimer = undefined;
    }
    void scheduleAuthAvailabilityProbe(controller);
  };

  window.addEventListener("auth:retry-now", handleRetryNow);

  return () => {
    controller.listeners.delete(listener);
    window.removeEventListener("auth:retry-now", handleRetryNow);
  };
}
